import { Injectable } from "@angular/core";
import { AdvancedConditions, CosmeticOptions, CustomRule, Filter } from "../../models/filter-models";
import { LabelSize, RuleListType, scaleLabelSize, YesNoAny } from "../../models/enums";
import { Comparator, Rarity } from "../../models/enums";
import { ItemData } from "../../models/item-data-models";
import { ItemDataService } from "../item-data.service";
import { poe1, poe2 } from "../../../game";
import { GeneralGeneratorService } from "./general-generator.service";
import { RuleId } from "../../models/rule-id";
import { CosmeticsGeneratorService } from "../cosmetics-generator.service";
import { EquipmentType } from "../../models/equipment-type.enum";
import { EquipmentTierService } from "../tiering/equipment-tier.service";
import { GenerationHelperService } from "./generation-helper.service";

@Injectable({
  providedIn: 'root'
})
export class RuleGeneratorService {
  private itemData!: ItemData[];
  private currencyItemTypes!: string[];

  constructor(private itemDataService: ItemDataService,
    private cosmeticGeneratorService: CosmeticsGeneratorService,
    private equipmentTierService: EquipmentTierService,
    private helper: GenerationHelperService) {
    this.itemData = this.itemDataService.getItemData();
    this.currencyItemTypes = this.itemDataService.getCurrencyItemTypes();
  }

  formatHighlightForTieredEquipment(rarity: Rarity, equipmentType: EquipmentType, minTier: number, filter: Filter, baseTypes: string[], classes: string, tier: number,
    minItemLevel?: number, minLinks?: number, baseAdvancedConditions?: AdvancedConditions, customLine?: string, id?: RuleId, name?: string, myEquipmentType?: 'weapon' | 'armour' | 'jewellery' | 'jewel' | 'flask', myEquipmentIndex?: number ) {

    if (baseTypes.length === 0) return '';

    if (minTier <= tier) {
      return this.generateEquipmentRule(equipmentType,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: rarity,
          advancedConditions: Object.assign(baseAdvancedConditions || {}, { minItemLevel, unidentifiedTier: tier, minLinks }) as AdvancedConditions,
          baseTypes: baseTypes,
        }),
        filter,
        name,
        { customClass: classes, customLine },
        id,
        undefined,
        myEquipmentType,
        myEquipmentIndex
      ) + '\n\n';
    }

    return '';
  }

  public generateEquipmentRule(equipmentType: EquipmentType, rule: CustomRule, filter: Filter, name?: string, options?: Partial<FormatRuleOptions>, ruleId?: RuleId, alwaysShow?: boolean, myEquipmentType?: 'weapon' | 'armour' | 'jewellery' | 'jewel' | 'flask', myEquipmentIndex?: number): string {
    const equipment = this.equipmentTierService.getMetadataForEquipmentType(equipmentType);
    if (!equipment) return '';

    return this.generateRule(new CustomRule({
      ...rule,
      displayOptions: this.helper.getEquipmentTierDisplayOptions(equipment, alwaysShow),
    }), filter, ruleId || equipment.ruleId, name || equipment.displayName, options, alwaysShow, myEquipmentType, myEquipmentIndex);
  }

  public generateRule(rule: CustomRule, filter: Filter, id?: RuleId, name?: string, options?: Partial<FormatRuleOptions>, alwaysShow?: boolean,
    ruleListType?: RuleListType, ruleListIndex?: number,
  ): string {
    if (!rule.displayOptions.active && !alwaysShow) return '';

    // Create a copy in case we change anything
    rule = structuredClone(rule);

    const optionsFromCustomRuleStyle = filter.customRuleStyles.find(s => s.ruleId === id)?.options;

    const { cosmeticOptions, hidden } = optionsFromCustomRuleStyle
      ? { cosmeticOptions: optionsFromCustomRuleStyle, hidden: false }
      : this.cosmeticGeneratorService.getCosmeticOptionsFromDisplayOptions(rule.displayOptions, undefined, name);

    const itemData = this.itemData.find(i => i.itemType === rule.itemType);
    const searchName = itemData?.currencySearchName;
    const showHide = hidden ? (filter.ruthless ? 'Minimal' : 'Hide') : 'Show';

    if (options?.overrideCosmetics) {
      for (const [key, value] of Object.entries(options.overrideCosmetics)) {
        if (value !== undefined && key in cosmeticOptions) {
          (cosmeticOptions as any)[key] = value;
        }
      }
    }

    let itemClass =
      options?.customClass
        ? `\n  Class == ${options.customClass}`
        : (rule.filterItemClass || rule.itemClass) === 'All' || rule.itemClass === ''
          ? ''
          : `\n  Class == ${rule.filterItemClass || '"' + rule.itemClass + '"'}`;

    const baseTypes =
      options?.customBaseTypes
        ? `\n  BaseType ${options.customBaseTypes}`
        : rule.filterItemBaseType
          ? `\n  BaseType ${rule.filterItemBaseType}`
        : (poe2 && rule.itemType === 'Skill Gems' && (rule.baseTypes.length === 0 || rule.baseTypes.includes('All')))
          ? '\n  BaseType == "Uncut Skill Gem" "Uncut Support Gem" "Uncut Spirit Gem"'
          : (!rule.baseTypes || rule.baseTypes.length === 0 || rule.baseTypes.includes('All'))
            ? rule.itemType === 'Currency'
              ? ''
              : (searchName && (rule.itemClass === 'Stackable Currency' || rule.itemClass === 'Skill Gems' || rule.itemClass === 'Support Gems' || rule.itemClass === 'Socketable' || rule.itemClass === 'Augment' || rule.itemClass === 'Amulets')) /// Amulets for Talismans
                ? searchName.includes('"')
                  ? `\n  BaseType ${searchName}`
                  : `\n  BaseType "${searchName}"`
                : ''
            : `\n  BaseType == ${rule.baseTypes.map(t => `"${t}"`).join(' ')}`;

    let rarity = (this.currencyItemTypes.includes(rule.itemType) || rule.itemClass === 'Stackable Currency')
      ? ''
      : (rule.rarityComparator === Comparator.GreaterThanOrEqual && rule.rarity === Rarity.Normal) // This is the default; no need to specify
        ? ''
        : `\n  Rarity ${rule.rarityComparator} ${rule.rarity}`;

    let customBeam = hidden || !cosmeticOptions?.highlight || !cosmeticOptions?.beam ? ''
      : `
  PlayEffect ${cosmeticOptions!.highlightColor}${cosmeticOptions!.temp ? ' Temp' : ''}`;

    const customMapIconSize = cosmeticOptions?.minimapIconSize || 1;
    const customMapIconColor = cosmeticOptions?.highlightColor || 'White';
    const customMapIconShape = cosmeticOptions?.minimapIconShape || 'Circle';

    let customMapIcon = hidden || !cosmeticOptions?.highlight ? ''
      : `
  MinimapIcon ${customMapIconSize} ${customMapIconColor} ${customMapIconShape}`;

    let customAlertSound = hidden || !cosmeticOptions?.alertSound ? ''
      : !cosmeticOptions?.customAlertSound ? `
  PlayAlertSound ${cosmeticOptions?.alertSoundSound} ${cosmeticOptions?.alertSoundVolume ?? 200}`
      : `
  CustomAlertSound "${cosmeticOptions?.customAlertSound}" ${cosmeticOptions?.alertSoundVolume ?? 200}`

    const disableDropSound = (rule.displayOptions.active && (hidden || cosmeticOptions?.disableDropSound)) ? '\n  DisableDropSound' : '';

    if (hidden) {
      customBeam = ``;
      customMapIcon = ``;
      customAlertSound = ``;
    }

    let fontSizeOrMinFontSize = cosmeticOptions?.fontSize;
    if (fontSizeOrMinFontSize) {
      fontSizeOrMinFontSize = scaleLabelSize(fontSizeOrMinFontSize, filter.cosmeticPresets.defaultLabelSize);
    }

    const textColor = cosmeticOptions?.textColor ? '\n  SetTextColor ' + this.formatColor(cosmeticOptions?.textColor) : '';
    const backgroundColor = cosmeticOptions?.backgroundColor ? '\n  SetBackgroundColor ' + this.formatColor(cosmeticOptions?.backgroundColor) : '';
    const borderColor = cosmeticOptions?.borderColor ? '\n  SetBorderColor ' + this.formatColor(cosmeticOptions?.borderColor) : '';
    const fontSize = fontSizeOrMinFontSize ? '\n  SetFontSize ' + fontSizeOrMinFontSize : '';
    const customLabel = textColor + backgroundColor + borderColor + fontSize;

    const advClasses = rule.advancedConditions?.classes ? '\n  Class ' + rule.advancedConditions?.classes : '';
    const advBaseTypes = rule.advancedConditions?.baseTypes ? '\n  BaseType ' + rule.advancedConditions?.baseTypes : '';
    const advMinItemLevel = rule.advancedConditions?.minItemLevel ? '\n  ItemLevel >= ' + rule.advancedConditions?.minItemLevel : '';
    const advMaxItemLevel = rule.advancedConditions?.maxItemLevel ? '\n  ItemLevel <= ' + rule.advancedConditions?.maxItemLevel : '';
    const advMinDropLevel = rule.advancedConditions?.minDropLevel ? '\n  DropLevel >= ' + rule.advancedConditions?.minDropLevel : '';
    const advMaxDropLevel = rule.advancedConditions?.maxDropLevel ? '\n  DropLevel <= ' + rule.advancedConditions?.maxDropLevel : '';
    const advMinAreaLevel = rule.advancedConditions?.minAreaLevel ? '\n  AreaLevel >= ' + rule.advancedConditions?.minAreaLevel : '';
    const advMaxAreaLevel = rule.advancedConditions?.maxAreaLevel ? '\n  AreaLevel <= ' + rule.advancedConditions?.maxAreaLevel : '';
    const advMinGemLevel = rule.advancedConditions?.minGemLevel ? '\n  GemLevel >= ' + rule.advancedConditions?.minGemLevel : '';
    const advMaxGemLevel = rule.advancedConditions?.maxGemLevel ? '\n  GemLevel <= ' + rule.advancedConditions?.maxGemLevel : '';
    const advUnidentifiedTier = rule.advancedConditions?.unidentifiedTier ? '\n  UnidentifiedItemTier >= ' + rule.advancedConditions?.unidentifiedTier : '';

    const advMinWaystoneTier = rule.advancedConditions?.waystoneMinTier ? (poe2 ? '\n  WaystoneTier ' : '\n  MapTier ') + '>= ' + rule.advancedConditions?.waystoneMinTier : '';
    const advMaxWaystoneTier = rule.advancedConditions?.waystoneMaxTier ? (poe2 ? '\n  WaystoneTier ' : '\n  MapTier ') + '<= ' + rule.advancedConditions?.waystoneMaxTier : '';
    const advAnyEnchantment = rule.advancedConditions?.anyEnchantment ? '\n  AnyEnchantment True' : '';
    const advBlighted = rule.advancedConditions?.blightedMap ? '\n  BlightedMap True' : '';
    const advBlightRavaged = rule.advancedConditions?.blightRavagedMap ? '\n  UberBlightedMap True' : '';
    const advZanaMemory = rule.advancedConditions?.zanaMemory ? '\n  ZanaMemory True' : '';
    const advMemoryStrands = rule.advancedConditions?.memoryStrands ? '\n  MemoryStrands >= ' + rule.advancedConditions?.memoryStrands : '';

    const advCorruptedImplicits = rule.advancedConditions?.corruptedImplicits ? '\n  CorruptedMods >= ' + rule.advancedConditions?.corruptedImplicits : '';
    let advHasExplicitMod = '';
    if (rule.advancedConditions?.hasExplicitModMin && !rule.advancedConditions?.hasExplicitMod) {
      advHasExplicitMod = '\n  HasExplicitMod >=' + rule.advancedConditions?.hasExplicitModMin + ' "a" "e" "i" "o" "u" "y"';
    } else if (rule.advancedConditions?.hasExplicitMod) {
      advHasExplicitMod = '\n  HasExplicitMod >=' + (rule.advancedConditions?.hasExplicitModMin ?? 0) + ' ' + rule.advancedConditions?.hasExplicitMod;
    }
    const advMinClusterJewelPassives = rule.advancedConditions?.minClusterJewelPassives ? '\n  EnchantmentPassiveNum >= ' + rule.advancedConditions?.minClusterJewelPassives : '';
    const advMaxClusterJewelPassives = rule.advancedConditions?.maxClusterJewelPassives ? '\n  EnchantmentPassiveNum <= ' + rule.advancedConditions?.maxClusterJewelPassives : '';
    const advClusterJewelPassiveNodes = rule.advancedConditions?.clusterJewelPassiveNodes?.length ?? 0 > 0 ? '\n  EnchantmentPassiveNode ' + rule.advancedConditions?.clusterJewelPassiveNodes!.map(n => `"${n}"`).join(' ') : '';

    let advEightMod = '';
    if (rule.advancedConditions?.eightModMap) {
      advEightMod = '\n  HasExplicitMod >=8 "a" "e" "i" "o" "u" "y"';
      rule.startWithClass = true;
    }

    const advTransfiguredGem = rule.advancedConditions?.transfiguredGem ? '\n  TransfiguredGem "' + rule.advancedConditions?.transfiguredGem + '"' : '';
    const advImbuedGem = rule.advancedConditions?.imbuedGem ? '\n  Imbued True' : '';

    let advInfluences = '';
    if (poe1 && rule.advancedConditions?.hasInfluence) {
      const allInfluences = 'Shaper Elder Crusader Hunter Redeemer Warlord';
      if (rule.advancedConditions.influences) {
        advInfluences = '\n  HasInfluence ' + (rule.advancedConditions.influences[0] === 'All' ? allInfluences : rule.advancedConditions.influences.join(' '));
      } else {
        advInfluences = '\n  HasInfluence ' + allInfluences;
      }
    }
    const advVeiled = poe2 || !rule.advancedConditions?.veiled ? '' : '\n  HasExplicitMod "Veil"';
    const advFractured = poe2 || !rule.advancedConditions?.fractured ? '' : '\n  FracturedItem True';
    const advSynthesised = poe2 || !rule.advancedConditions?.synthesised ? '' : '\n  SynthesisedItem True';

    let advIdentified = rule.advancedConditions?.identified && rule.advancedConditions?.identified !== YesNoAny.Any ? '\n  Identified ' + (rule.advancedConditions?.identified === YesNoAny.Yes ? 'True' : 'False') : '';
    let advCorrupted = (rule.advancedConditions?.corrupted && rule.advancedConditions?.corrupted !== YesNoAny.Any) ? '\n  Corrupted ' + (rule.advancedConditions?.corrupted === YesNoAny.Yes ? 'True' : 'False') : '';
    const advMinQuality = rule.advancedConditions?.minQuality ? '\n  Quality >= ' + rule.advancedConditions?.minQuality : '';
    const advSocketGroup = rule.advancedConditions?.socketGroup ? '\n  SocketGroup ' + GeneralGeneratorService.formatSocketGroup(rule.advancedConditions?.socketGroup) : '';
    const advMinLinks = rule.advancedConditions?.minLinks ? '\n  LinkedSockets >= ' + rule.advancedConditions?.minLinks : '';
    const advMinSockets = rule.advancedConditions?.minSockets ? '\n  Sockets >= ' + rule.advancedConditions.minSockets : '';
    const advMinStackSize = rule.advancedConditions?.minStackSize ? '\n  StackSize >= ' + rule.advancedConditions?.minStackSize : '';
    const advMinBaseDefencePercentile = rule.advancedConditions?.minBaseDefencePercentile ? '\n  BaseDefencePercentile >= ' + rule.advancedConditions?.minBaseDefencePercentile : '';
    const advMinArmour = (rule.advancedConditions?.minArmour === null || rule.advancedConditions?.minArmour === undefined) ? '' : rule.advancedConditions?.minArmour === 0 ? '\n  BaseArmour == 0' : '\n  BaseArmour >= ' + rule.advancedConditions?.minArmour;
    const advMinEvasion = (rule.advancedConditions?.minEvasion === null || rule.advancedConditions?.minEvasion === undefined) ? '' : rule.advancedConditions?.minEvasion === 0 ? '\n  BaseEvasion == 0' : '\n  BaseEvasion >= ' + rule.advancedConditions?.minEvasion;
    const advMinEnergyShield = (rule.advancedConditions?.minEnergyShield === null || rule.advancedConditions?.minEnergyShield === undefined) ? '' : rule.advancedConditions?.minEnergyShield === 0 ? '\n  BaseEnergyShield == 0' : '\n  BaseEnergyShield >= ' + rule.advancedConditions?.minEnergyShield;
    const advMinWard = poe2 ? '' : (rule.advancedConditions?.minWard === null || rule.advancedConditions?.minWard === undefined) ? '' : rule.advancedConditions?.minWard === 0 ? '\n  BaseWard == 0' : '\n  BaseWard >= ' + rule.advancedConditions?.minWard;
    const advFoulbornUnique = rule.advancedConditions?.foulbornUnique ? '\n  Foulborn True' : '';
    const advIsVaalUnique = rule.advancedConditions?.isVaalUnique ? '\n  IsVaalUnique True' : '';
    const advHasVaalUniqueMod = rule.advancedConditions?.hasVaalUniqueMod ? '\n  HasVaalUniqueMod True' : '';

    const continueRule = rule.continue ? '\n  Continue' : '';

    let startWithClass = '', startWithIdentified = '', startWithCorrupted = '', startWithRarity = '';
    if (rule.startWithClass) {
      startWithClass = itemClass;
      itemClass = '';
    }

    if (rule.advancedConditions?.identified === YesNoAny.Yes) {
      startWithIdentified = advIdentified;
      advIdentified = '';
    }

    if (rule.advancedConditions?.corrupted === YesNoAny.Yes) {
      startWithCorrupted = advCorrupted;
      advCorrupted = '';
    }

    if (rule.rarity === Rarity.Unique) {
      startWithRarity = rarity;
      rarity = '';
    }

    let ruleName = name ? ` # ${name}` : '';
    if (id) {
      if (ruleListType && ruleListIndex !== undefined) {
        ruleName += ` (${id}/${ruleListType}/${ruleListIndex + 1})`;
      } else {
        ruleName += ` (${id})`;
      }
    }

    // More unlikely conditions first to improve performance: https://www.reddit.com/r/pathofexile/comments/5ftpbg/lootfilter_performance_tips_thanks_to_the/
    return `
${showHide}${ruleName}${startWithIdentified}${startWithCorrupted}${startWithRarity}${startWithClass}${advInfluences}${advFractured}${advSynthesised}${advAnyEnchantment}${advImbuedGem}${advCorruptedImplicits}${advZanaMemory}${advBlighted}${advBlightRavaged}${advEightMod}${advMemoryStrands}${advTransfiguredGem}${advUnidentifiedTier}${advIsVaalUnique}${advHasVaalUniqueMod}${advFoulbornUnique}${advMinQuality}${advSocketGroup}${advMinLinks}${advMinSockets}${advMinStackSize}${advMinBaseDefencePercentile}${advMinArmour}${advMinEvasion}${advMinEnergyShield}${advMinWard}${advMinWaystoneTier}${advMaxWaystoneTier}${advMinItemLevel}${advMaxItemLevel}${advMinDropLevel}${advMaxDropLevel}${advMinAreaLevel}${advMaxAreaLevel}${advMinGemLevel}${advMaxGemLevel}${itemClass}${advClasses}${baseTypes}${advBaseTypes}${rarity}${advVeiled}${advCorrupted}${advIdentified}${advHasExplicitMod}${advMinClusterJewelPassives}${advMaxClusterJewelPassives}${advClusterJewelPassiveNodes}${options?.customLine || ''}${customBeam}${customMapIcon}${customLabel}${customAlertSound}${disableDropSound}${continueRule}`;
  }

  public formatColor(color: string) {
    if (color.startsWith('#')) {
      if (color.length === 7) {
        // #RRGGBB
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `${r} ${g} ${b} 255`;
      } else if (color.length === 9) {
        // #RRGGBBAA
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        const a = parseInt(color.slice(7, 9), 16);
        return `${r} ${g} ${b} ${a}`;
      }
    } else if (color.startsWith('rgb')) {
      const rgbaColor = color.replace('rgba(', '').replace('rgb(', '').replace(')', '').split(',').map((c, index) => index === 3 ? parseFloat(c) : parseInt(c));
      const alpha = rgbaColor.length === 4 ? Math.round(rgbaColor[3] * 255) : 255;
      return `${rgbaColor[0]} ${rgbaColor[1]} ${rgbaColor[2]} ${alpha}`;
    }
    throw new Error('Invalid color format');
  }
}

export interface FormatRuleOptions {
  customClass?: string;
  customBaseTypes?: string;
  customLine?: string;
  overrideCosmetics?: Partial<CosmeticOptions>;
}
